import { supabase } from '../lib/supabase';

// Helper function to convert snake_case to camelCase
const toCamelCase = (obj) => {
  if (!obj) return obj;
  if (Array.isArray(obj)) return obj?.map(toCamelCase);
  if (typeof obj !== 'object') return obj;

  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    acc[camelKey] = toCamelCase(obj?.[key]);
    return acc;
  }, {});
};

// Helper function to convert camelCase to snake_case
const toSnakeCase = (obj) => {
  if (!obj) return obj;
  if (Array.isArray(obj)) return obj?.map(toSnakeCase);
  if (typeof obj !== 'object') return obj;

  return Object.keys(obj)?.reduce((acc, key) => {
    const snakeKey = key?.replace(/[A-Z]/g, (letter) => `_${letter?.toLowerCase()}`);
    acc[snakeKey] = toSnakeCase(obj?.[key]);
    return acc;
  }, {});
};

// Deals Service
export const dealsService = {
  async getAll() {
    const { data, error } = await supabase?.from('deals')?.select('*')?.order('created_at', { ascending: false });
    
    if (error) throw error;
    return toCamelCase(data);
  },

  async create(deal) {
    const dealData = toSnakeCase(deal);
    const { data, error } = await supabase?.from('deals')?.insert(dealData)?.select()?.single();
    
    if (error) throw error;
    return toCamelCase(data);
  },

  async update(id, updates) {
    const updateData = toSnakeCase(updates);
    const { data, error } = await supabase?.from('deals')?.update(updateData)?.eq('id', id)?.select()?.single();
    
    if (error) throw error;
    return toCamelCase(data);
  },

  async delete(id) {
    const { error } = await supabase?.from('deals')?.delete()?.eq('id', id);
    
    if (error) throw error;
  },

  subscribeToChanges(callback) {
    const channel = supabase?.channel('deals-changes')?.on('postgres_changes', 
        { event: '*', schema: 'public', table: 'deals' },
        (payload) => {
          callback({
            eventType: payload?.eventType,
            new: toCamelCase(payload?.new),
            old: toCamelCase(payload?.old)
          });
        }
      )?.subscribe();

    return () => supabase?.removeChannel(channel);
  }
};

// Contacts Service
export const contactsService = {
  async getAll() {
    const { data, error } = await supabase?.from('contacts')?.select('*')?.order('created_at', { ascending: false });
    
    if (error) throw error;
    return toCamelCase(data);
  },

  async create(contact) {
    const contactData = toSnakeCase(contact);
    const { data, error } = await supabase?.from('contacts')?.insert(contactData)?.select()?.single();
    
    if (error) throw error;
    return toCamelCase(data);
  },

  async update(id, updates) {
    const updateData = toSnakeCase(updates);
    const { data, error } = await supabase?.from('contacts')?.update(updateData)?.eq('id', id)?.select()?.single();
    
    if (error) throw error;
    return toCamelCase(data);
  },

  async delete(id) {
    const { error } = await supabase?.from('contacts')?.delete()?.eq('id', id);
    
    if (error) throw error;
  },

  subscribeToChanges(callback) {
    const channel = supabase?.channel('contacts-changes')?.on('postgres_changes',
        { event: '*', schema: 'public', table: 'contacts' },
        (payload) => {
          callback({
            eventType: payload?.eventType,
            new: toCamelCase(payload?.new),
            old: toCamelCase(payload?.old)
          });
        }
      )?.subscribe();

    return () => supabase?.removeChannel(channel);
  }
};

// Appointments Service
export const appointmentsService = {
  async getAll() {
    const { data, error } = await supabase?.from('appointments')?.select('*')?.order('date', { ascending: true });
    
    if (error) throw error;
    return toCamelCase(data);
  },

  async create(appointment) {
    const appointmentData = toSnakeCase(appointment);
    const { data, error } = await supabase?.from('appointments')?.insert(appointmentData)?.select()?.single();
    
    if (error) throw error;
    return toCamelCase(data);
  },

  async update(id, updates) {
    const updateData = toSnakeCase(updates);
    const { data, error } = await supabase?.from('appointments')?.update(updateData)?.eq('id', id)?.select()?.single();
    
    if (error) throw error;
    return toCamelCase(data);
  },

  async delete(id) {
    const { error } = await supabase?.from('appointments')?.delete()?.eq('id', id);
    
    if (error) throw error;
  },

  subscribeToChanges(callback) {
    const channel = supabase?.channel('appointments-changes')?.on('postgres_changes',
        { event: '*', schema: 'public', table: 'appointments' },
        (payload) => {
          callback({
            eventType: payload?.eventType,
            new: toCamelCase(payload?.new),
            old: toCamelCase(payload?.old)
          });
        }
      )?.subscribe();

    return () => supabase?.removeChannel(channel);
  }
};

// Projects Service
export const projectsService = {
  async getAll() {
    const { data, error } = await supabase?.from('projects')?.select('*')?.order('created_at', { ascending: false });
    
    if (error) throw error;
    return toCamelCase(data);
  },

  async create(project) {
    const projectData = toSnakeCase(project);
    const { data, error } = await supabase?.from('projects')?.insert(projectData)?.select()?.single();
    
    if (error) throw error;
    return toCamelCase(data);
  },

  async update(id, updates) {
    const updateData = toSnakeCase(updates);
    const { data, error } = await supabase?.from('projects')?.update(updateData)?.eq('id', id)?.select()?.single();
    
    if (error) throw error;
    return toCamelCase(data);
  },

  async delete(id) {
    const { error } = await supabase?.from('projects')?.delete()?.eq('id', id);
    
    if (error) throw error;
  },

  subscribeToChanges(callback) {
    const channel = supabase?.channel('projects-changes')?.on('postgres_changes',
        { event: '*', schema: 'public', table: 'projects' },
        (payload) => {
          callback({
            eventType: payload?.eventType,
            new: toCamelCase(payload?.new),
            old: toCamelCase(payload?.old)
          });
        }
      )?.subscribe();

    return () => supabase?.removeChannel(channel);
  }
};

export default {
  deals: dealsService,
  contacts: contactsService,
  appointments: appointmentsService,
  projects: projectsService
};